import React, { Children, type ReactNode } from 'react';
import { ContextualAnalyticsData } from '@atlassian/jira-product-analytics-bridge';
import { useContainerContext as useContainerContextDI } from '@atlassian/jira-providers-container-context/src/main.tsx';
import {
	useServiceDeskAnalyticsContext as useServiceDeskAnalyticsContextDI,
	type ServiceDeskAnalyticAttributes,
} from '@atlassian/jira-router-resources-service-desk-analytics-context/src/services/index.tsx';
// eslint-disable-next-line jira/import/file-extension-required
import type { DataState } from '@atlassian/jira-common-services/src/types';
// eslint-disable-next-line jira/import/file-extension-required
import type { UseContainerContextReturn } from '@atlassian/jira-providers-container-context/src/types';

type Props = {
	children: ReactNode;
	forceRenderWhileLoading: boolean;
	useServiceDeskAnalyticsContext: typeof useServiceDeskAnalyticsContextDI;
	useContainerContext: typeof useContainerContextDI;
};

export const ServiceDeskAnalyticsContext: {
	({
		useContainerContext,
		useServiceDeskAnalyticsContext,
		children,
		forceRenderWhileLoading,
	}: Props): React.JSX.Element | null;
	defaultProps: {
		forceRenderWhileLoading: boolean;
		useServiceDeskAnalyticsContext: () => DataState<ServiceDeskAnalyticAttributes>;
		useContainerContext: () => UseContainerContextReturn;
	};
} = ({
	useContainerContext,
	useServiceDeskAnalyticsContext,
	children,
	forceRenderWhileLoading,
}: Props): React.JSX.Element | null => {
	// eslint-disable-next-line react-hooks/react-compiler
	const [{ data: containerContextData, loading: containerContextLoading }] = useContainerContext();

	const {
		data: analyticsContextAttributes,
		error: analyticsContextError,
		loading: analyticsContextLoading,
		// eslint-disable-next-line react-hooks/react-compiler
	} = useServiceDeskAnalyticsContext();

	if (Children.count(children) > 0) {
		const loading = containerContextLoading || analyticsContextLoading;
		const shouldRenderChildren =
			!loading || (loading && forceRenderWhileLoading) || !!analyticsContextError;

		if (!shouldRenderChildren) {
			return null;
		}

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const context: Record<string, any> = {};
		// @ts-expect-error - TS2339 - Property 'project' does not exist on type 'ContainerContext'.
		if (containerContextData && containerContextData.project) {
			// @ts-expect-error - TS2339 - Property 'project' does not exist on type 'ContainerContext'.
			const { id, type } = containerContextData.project;
			// containerType and containerId are obsolete - See https://developer.atlassian.com/platform/analytics-pipeline/getting-started/event-types/#glossary-of-terms
			// Also these dont work with screen events. See sendScreenEvent in: https://bitbucket.org/atlassian/analytics-web-client/src/master/src/analyticsWebClient.js
			// However they are kept here to not break any existing usages.
			context.containerType = 'project';
			context.containerId = `${id}`;
			// This is the preferred way to send container data for analytics
			context.containers = {
				project: {
					id: `${id}`,
					type,
				},
			};

			if (analyticsContextAttributes && !analyticsContextError) {
				context.attributes = analyticsContextAttributes;
			}
		}

		return <ContextualAnalyticsData {...context}>{children}</ContextualAnalyticsData>;
	}
	return <>{children}</>;
};

ServiceDeskAnalyticsContext.defaultProps = {
	forceRenderWhileLoading: false,
	useServiceDeskAnalyticsContext: useServiceDeskAnalyticsContextDI,
	useContainerContext: useContainerContextDI,
};
